import { useParseMeta } from "@2u-landing-pages/providers";
import { ContentfulImageConfig } from "@2u-landing-pages/types";
import { renderEmbeddedEntry, hyperLinkRenderer, renderMark, renderText } from "@2u-landing-pages/utils/contentful-richtext";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { BLOCKS, INLINES } from "@contentful/rich-text-types";
import { Card as BricksCard } from "@red-digital/bricks";
import cn from "classnames";
import { forwardRef } from "react";
import { ContentfulImage } from "../../../ContentfulImage";
import { Icon } from "../../../Icon";
import { Initials } from "../../../Initials";
import { CardVariantProps } from "../../Card.type";
// @ts-ignore
import cardStyles from "../../Card.module.scss";
// @ts-ignore
import smallCardStyles from "./SmallCard.module.scss";

export const SmallCard = forwardRef<HTMLDivElement, CardVariantProps>(({
  entry,
  heading,
  subhead,
  body,
  className,
  style,
  iconSelectionData,
  phoneNumber,
  inspector,
}, ref) => {
  const parseMetadata = useParseMeta();
  const {
    initials,
    image,
    text_alignment = "center",
    image_config,
    heading: contentfulHeading,
    subhead: contentfulSubhead,
    body: contentfulBody,
  } = entry.fields;
  const selectedIcon = iconSelectionData?.icon ?? "";
  const selectedIconSize = iconSelectionData?.size || undefined;

  const imageConfig = image_config as unknown as ContentfulImageConfig;
  const desktopCfg = imageConfig?.default;
  const mobileCfg = imageConfig?.mobile;
  const isSvgImage = image?.fields?.file?.contentType === "image/svg+xml";

  return (
    <BricksCard
      ref={ref}
      style={style}
      className={cn(cardStyles.card, cardStyles[`text-${text_alignment}`], smallCardStyles.small, className, {
        [smallCardStyles.initials]: Boolean(initials) && !image,
        [cardStyles.solidOverlay]: [desktopCfg, mobileCfg].some((cfg) => cfg?.gradientType === "solid"),
        [cardStyles.gradientOverlay]: [desktopCfg, mobileCfg].some((cfg) => cfg?.gradientType === "gradient"),
      })}
      layout={{
        breakpoint: "lg",
      }}
      slots={{
        start: {
          component: (
            <>
              {!image && !selectedIcon && initials && (
                <Initials
                  initials={initials}
                  size="small"
                  {...inspector({
                    entryId: entry.sys.id,
                    fieldId: "initials",
                  })}
                />
              )}
              {!image && selectedIcon && (
                <Icon
                  name={selectedIcon}
                  size={selectedIconSize}
                  {...inspector({
                    entryId: entry.sys.id,
                    fieldId: "iconSelection",
                  })}
                />
              )}
            </>
          ),
        },
      }}
      media={
        image?.fields?.file?.url
          ? {
              component: (
                <ContentfulImage
                  image={image}
                  config={image_config}
                  className={cn({ [cardStyles.svgImage]: isSvgImage })}
                  {...inspector({
                    entryId: entry.sys.id,
                    fieldId: "image",
                  })}
                  style={style}
                />
              ),
              position: "beforeContent",
            }
          : undefined
      }
      content={{
        headingProps: {
          variant: "heading-lg",
          className: cardStyles.heading,
          ...heading,
          children: contentfulHeading
            ? documentToReactComponents(contentfulHeading, {
                renderMark,
                renderText: (text) => renderText(parseMetadata ? parseMetadata(text) : text, phoneNumber),
                renderNode: {
                  [BLOCKS.PARAGRAPH]: (node, children) => children,
                  [INLINES.EMBEDDED_ENTRY]: (node) =>
                    renderEmbeddedEntry(node, "Card Small Heading", contentfulHeading, phoneNumber, parseMetadata),
                },
              })
            : undefined,
          ...inspector({
            entryId: entry.sys.id,
            fieldId: "heading",
          }),
        },
        subheadProps: contentfulSubhead
          ? {
              variant: "subhead-md",
              className: cardStyles.subhead,
              ...subhead,
              children: documentToReactComponents(contentfulSubhead, {
                renderMark,
                renderText: (text) => renderText(parseMetadata ? parseMetadata(text) : text, phoneNumber),
                renderNode: {
                  [BLOCKS.PARAGRAPH]: (node, children) => children,
                  [INLINES.EMBEDDED_ENTRY]: (node) =>
                    renderEmbeddedEntry(node, "Card Small Subhead", contentfulSubhead, phoneNumber, parseMetadata),
                },
              }),
              ...inspector({
                entryId: entry.sys.id,
                fieldId: "subhead",
              }),
            }
          : undefined,
        descriptionProps: {
          modifier: 500,
          variant: "body-md",
          className: cardStyles.body,
          ...body,
          type: "div",
          children: contentfulBody
            ? documentToReactComponents(contentfulBody, {
                renderMark,
                renderText: (text) => renderText(parseMetadata ? parseMetadata(text) : text, phoneNumber),
                renderNode: {
                  [BLOCKS.PARAGRAPH]: (node, children) => children,
                  [INLINES.EMBEDDED_ENTRY]: (node) =>
                    renderEmbeddedEntry(node, "Card Small Body", contentfulBody, phoneNumber, parseMetadata),
                  [INLINES.HYPERLINK]: (node) => hyperLinkRenderer(node, "Card Small - Body"),
                },
              })
            : undefined,
          ...inspector({
            entryId: entry.sys.id,
            fieldId: "body",
          }),
        },
      }}
    />
  );
});

SmallCard.displayName = "SmallCard";
